import React, { useState, useRef, useEffect, useCallback } from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import 'url-search-params-polyfill'; 
import axios from 'axios';
import {
  Divider,
  Drawer,
  CssBaseline,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useTheme,
  useMediaQuery,
  Box,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import SessionTimeout from './SessionTimeout';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import HomeIcon from '@mui/icons-material/Home';
import InboxIcon from '@mui/icons-material/MoveToInbox';
import SendIcon from '@mui/icons-material/Send';
import MenuIcon from '@mui/icons-material/Menu';
import CreateIcon from '@mui/icons-material/Create';
import {useIdleTimer} from 'react-idle-timer';
import EmailList from './EmailList';
import Email from './Email';
import Compose from './Compose';

const drawerWidth = 240;
const desktopDrawerWidth = '20vw';

// Styled components for MUI v7
const Root = styled('div')(({ theme }) => ({
  display: 'flex',
}));

const StyledAppBar = styled(AppBar)(({ theme }) => ({
  zIndex: theme.zIndex.drawer + 1,
  boxShadow: theme.shadows[2],
}));

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
  justifyContent: 'flex-end',
}));

const StyledDrawer = styled(Drawer)(({ theme }) => ({
  flexShrink: 0,
  width: drawerWidth,
  '& .MuiDrawer-paper': {
    width: drawerWidth,
    borderRight: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.default,
  },
  [theme.breakpoints.up('laptop')]: {
    width: desktopDrawerWidth,
    '& .MuiDrawer-paper': {
      width: desktopDrawerWidth,
      borderRight: `1px solid ${theme.palette.divider}`,
      backgroundColor: theme.palette.background.default,
    },
  },
}));

const Content = styled('div')(({ theme }) => ({
  flexGrow: 1,
  padding: theme.spacing(2),
  maxWidth: '1400px',
  margin: '0 auto',
}));

const MenuButton = styled(IconButton)(({ theme }) => ({
  marginRight: theme.spacing(2),
  [theme.breakpoints.up('laptop')]: {
    display: 'none',
  },
}));

const HomeButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  right: '12px',
  [theme.breakpoints.down('tablet')]: {
    top: '-1px',
  },
  [theme.breakpoints.between('tablet', 'laptop')]: {
    top: '8px',
  },
  [theme.breakpoints.up('laptop')]: {
    display: 'none',
  },
}));

const ComposeButton = styled(Button)(({ theme }) => ({
  marginTop: theme.spacing(1.25),
  marginLeft: theme.spacing(4),
  textAlign: 'center',
  borderRadius: theme.shape.borderRadius * 2,
  textTransform: 'none',
  fontWeight: 600,
}));

const MailboxList = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('tablet'));
  const isTablet = useMediaQuery(theme.breakpoints.between('tablet', 'laptop'));
  const isLaptopUp = useMediaQuery(theme.breakpoints.up('laptop'));

  // Reset view when transitioning between layouts to ensure list is visible
  useEffect(() => {
    setState((prev) => ({
      ...prev,
      showMobileEmail: isMobile ? prev.showMobileEmail : false,
      initialEmailView: isLaptopUp ? prev.initialEmailView : prev.initialEmailView,
    }));
  }, [isMobile, isTablet, isLaptopUp]);
  // Refs
  const idleTimerRef = useRef(null);
  const emailListRef = useRef(null);
  const emailListRefMobile = useRef(null);

  // State
  const [state, setState] = useState({
    Access: false,
    userName: '',
    email: '',
    mid: '',
    mobileOpen: false,
    isCompose: true,
    senderName: '',
    subject: '',
    body: '',
    date: '',
    time: '',
    attachments: [],
    folder: 'Inbox',
    selectedIndex: 1,
    initialEmailView: false,
    showMobileEmail: false,
    loadingEmail: false, // <-- loading spinner state
    timeout: 1000 * 600 * 1,
    showModal: false,
    userLoggedIn: false,
    isTimedOut: false,
  });

  // Generate Token
  const generateToken = useCallback(() => {
    const body = {
      client_id: process.env.REACT_APP_CLIENT_ID,
      client_secret: process.env.REACT_APP_CLIENT_SECRET,
      grant_type: process.env.REACT_APP_GRANT_TYPE,
    };
    const config = {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Access-Control-Allow-Origin': '*',
        'Cache-Control': 'No-Store',
      },
    };
    const form = new URLSearchParams(body).toString();

  axios
    .post(process.env.REACT_APP_GENERATE_TOKEN_URL, form, config)
    .then((res) => {
      sessionStorage.setItem('token', res.data.access_token);
    })
    .catch((error) => {
      console.error('Token generation error:', error);
    });
}, []);

  // Claim Mailbox
  const claimMailbox = useCallback((claimToken) => {
    const bodyData = {
        claim_token: claimToken,
      };
    // console.log('[mailbox] claimMailbox invoked', { claimToken });
    axios
      .put(process.env.REACT_APP_CLAIM_MAILBOX_URL, bodyData, {
        headers: {
          Authorization: 'Bearer ' + sessionStorage.getItem('token'),
          'Content-Type': 'application/json',
          'Access-Control-Allow-Origin': '*',
          'Cache-Control': 'No-Store',
        },
      })
      .then((res) => {
        // console.log('[mailbox] claimMailbox success', res.data);
        setState((prev) => ({
          ...prev,
          Access: true,
          userName: res.data.name,
          email: res.data.email,
          mid: res.data.mid,
        }));
      })
      .catch((err) => {
        console.error('Error claiming mailbox:', err);
        alert('There was an error claiming the mailbox');
        window.location.replace(process.env.REACT_APP_REDIRECTED_APP_URL);
      });
  }, []);

  // Create Mailbox
  const createMailbox = useCallback(
    (name, email) => {
      const bodyData = {
        name: name,
        email: email,
        space_id: process.env.REACT_APP_SPACE_ID,
      };
      // console.log('[mailbox] createMailbox payload', bodyData);
      axios
        .post(process.env.REACT_APP_OMS_MAILBOX_URL, bodyData, {
          headers: {
            Authorization: 'Bearer ' + sessionStorage.getItem('token'),
            'Content-Type': 'application/json',
            'Access-Control-Allow-Origin': '*',
            'Cache-Control': 'No-Store',
          },
        })
        .then((res) => {
            // console.log('[mailbox] createMailbox success - invoking claimMailbox', res.data);
          claimMailbox(res.data.claim_token);
        })
        .catch((err) => {
          console.error('Error creating mailbox:', err);
          alert('There was an error creating new mailbox.');
          window.location.replace(process.env.REACT_APP_REDIRECTED_APP_URL);
        });
    },
    [claimMailbox]
  );

  // Check Token
  const checkToken = useCallback(
    (token, name, email) => {
      // console.log('[mailbox] checkToken start', { email });
      axios
        .get(process.env.REACT_APP_OMS_MAILBOX_URL, {
          params: {
            space_id: process.env.REACT_APP_SPACE_ID,
            email: email,
          },
          headers: {
            Authorization: 'Bearer ' + token,
            'Access-Control-Allow-Origin': '*',
            'Cache-Control': 'No-Store',
          },
        })
        .then((res) => {
          sessionStorage.setItem('token', token);
          setInterval(() => {
            generateToken();
          }, 1800000);

          if (res.data.mailbox.length !== 0) {
            // console.log('[mailbox] existing mailbox found', res.data.mailbox[0]);
            setState((prev) => ({
              ...prev,
              Access: true,
              userName: res.data.mailbox[0].name,
              email: res.data.mailbox[0].email,
              mid: res.data.mailbox[0].mid,
            }));
          } else {
            // console.log('[mailbox] no mailbox found -> creating new mailbox', { name, email });
            createMailbox(name, email);
          }
        })
        .catch((err) => {
          setState((prev) => ({ ...prev, Access: false }));
          console.error('Error checking mailbox token:', err);
          alert('You are unauthorized to use this service from checkToken.');
          window.location.replace(process.env.REACT_APP_REDIRECTED_APP_URL);
        });
    },
    [generateToken, createMailbox]
  );

  // Component Mount - Check URL parameters
  useEffect(() => {
    // const url = decodeURIComponent(window.location.search);
    let url = decodeURIComponent(window.location.search);
  // If no params, mock them for local dev
  // if (!url || url === "") {
  //    url = "?email=reddy_manikanth@optum.com&name=Wanda%20Dowless&token=VhceKYA3sx5GgUIp73DPHX3f-aY4u_n-IiXcyhhfUmh-Wdnyw1GfuMV9C24g";
  //   window.history.replaceState({}, '', url); // Update browser URL
  // }
    
    const params = new URLSearchParams(url);
    const paramToken = params.get('token');
    const paramEmail = params.get('email');
    // const paramEmail = 'anitha_tr@optum.com';
    const paramName = params.get('name');

    if (
      paramToken !== null &&
      paramToken !== '' &&
      paramEmail !== null &&
      paramEmail !== '' &&
      paramName !== null &&
      paramName !== ''
    ) {
      checkToken(paramToken, paramName, paramEmail);
    } else {
     window.location.replace(process.env.REACT_APP_REDIRECTED_APP_URL);
    }
  }, [checkToken]);

  // Component Unmount - Clear token
  useEffect(() => {
    return () => {
      sessionStorage.setItem('token', '');
    };
  }, []);

  // Handle Folder Selection
  const handleFolder = (value, i) => {
    setState((prev) => ({
      ...prev,
      folder: value,
      isCompose: false,
      selectedIndex: i,
      initialEmailView: false,
      showMobileEmail: false,
      mobileOpen: false, // close drawer on mobile
    }));

    setTimeout(() => {
      // For mobile view
      if (emailListRefMobile.current) {
        emailListRefMobile.current.clearFolder();
        emailListRefMobile.current.getEmailList();
      }
      // For Desktop view
      if (emailListRef.current) {
        emailListRef.current.clearFolder();
        emailListRef.current.getEmailList();
      }
    }, 100);
  };

  // Handle Drawer Toggle
  const handleDrawerToggle = () => {
    setState((prev) => ({ ...prev, mobileOpen: !prev.mobileOpen }));
  };

  // Show Compose
  const showCompose = () => {
    setState((prev) => ({
      ...prev,
      selectedIndex: null,
      isCompose: true,
      showMobileEmail: false,
      initialEmailView: false,
      mobileOpen: false,
    }));
  };

  // Handle Initial Email View
  const handleInitialEmailView = useCallback((value) => {
    setState((prev) => ({ ...prev, initialEmailView: value }));
  }, []);

  // Handle Mobile Email View
  const handleMobileEmailView = useCallback((value) => {
    setState((prev) => ({ ...prev, showMobileEmail: value }));
  }, []);

  // Handle Home Button
  const handleHomeButton = () => {
    setState((prev) => ({ ...prev, showMobileEmail: false, isCompose: true }));
  };

  // Get Email Props
  const getEmailProps = useCallback((senderName, subject, body, sentDate, attachments) => {
    let sdate = '' + sentDate;
    let time = sdate.substring(11, 16);
    sdate = sdate.substring(0, 10);
    setState((prev) => ({
      ...prev,
      senderName: senderName,
      subject: subject,
      body: body,
      date: sdate + ' ' + time,
      time: time,
      attachments: attachments,
      loadingEmail: false, // stop spinner when loaded
      initialEmailView: true,
      showMobileEmail: true,
    }));
  }, []);

  // Idle Timer Handlers
  const onAction = useCallback(() => {
    setState((prev) => ({ ...prev, isTimedOut: false }));
  }, []);

  const onActive = useCallback(() => {
    setState((prev) => ({ ...prev, isTimedOut: false }));
  }, []);

   
  const onIdle = useCallback(() => {
  setState((prev) => {
    if (prev.isTimedOut) {
      alert('Idle timeOut');
      return prev;
    }
    idleTimer.reset();
    return { ...prev, showModal: true, isTimedOut: true };
  });
}, []);

const idleTimer = useIdleTimer({
  timeout: state.timeout,
  debounce: 250,
  onIdle,
  onActive,
  onAction,
});


  // Handle Modal Close
  const handleClose = () => {
    setState((prev) => ({ ...prev, showModal: false }));
  };

  // Handle Logout
  const handleLogout = () => {
    setState((prev) => ({ ...prev, showModal: false }));
    alert('Logged out');
     window.location.replace(process.env.REACT_APP_REDIRECTED_APP_URL);
  };

  // Don't render if not authenticated
  if (state.Access !== true) {
    return null;
  }
  

  const container = window !== undefined ? () => window.document.body : undefined;

  // Responsive View Logic
  let mainContent = null;

  if (isMobile) {
    let mobileView;
    if (state.loadingEmail) {
      mobileView = (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <CircularProgress size={48} />
        </Box>
      );
    } else if (state.showMobileEmail && state.initialEmailView) {
      mobileView = (
        <Email
          name={state.senderName}
          subject={state.subject}
          body={state.body}
          date={state.date}
          attachments={state.attachments}
          initialEmailView={state.initialEmailView}
          handleInitialEmailView={() => {
            setState((prev) => ({ ...prev, showMobileEmail: false, initialEmailView: false }));
          }}
          handleMobileEmailView={handleMobileEmailView}
          showBackButton={true}
        />
      );
    } else {
      mobileView = (
        <EmailList
          ref={emailListRefMobile}
          getEmailProps={(...args) => {
            setState((prev) => ({ ...prev, loadingEmail: true }));
            getEmailProps(...args);
          }}
          time={state.time}
          handleInitialEmailView={handleInitialEmailView}
          mid={state.mid}
          folder={state.folder}
          handleMobileEmailView={handleMobileEmailView}
        />
      );
    }

    mainContent = (
      <Box sx={{ mt: 8, px: 2, width: '100%' }}>
        {mobileView}
      </Box>
    );
  } else if (isTablet) {
    mainContent = (
      <Box
        sx={{
          mt: 8,
          px: { tablet: 1, laptop: 2 },
          width: '100%',
          display: 'flex',
          gap: 2,
          height: 'calc(100vh - 120px)',
          fontSize: { tablet: '0.95rem', laptop: '1rem' },
        }}
      >
        <Box sx={{ flex: '0 0 35%', minWidth: 0, borderRight: '1px solid #e0e0e0' }}>
          <EmailList
            ref={emailListRef}
            getEmailProps={getEmailProps}
            handleMobileEmailView={handleMobileEmailView}
            handleInitialEmailView={handleInitialEmailView}
            time={state.time}
            mid={state.mid}
            folder={state.folder}
          />
        </Box>
        <Box sx={{ flex: '1 1 65%', minWidth: 0, px: 2, display: 'flex', justifyContent: 'center' }}>
          {state.initialEmailView ? (
            <Email
              name={state.senderName}
              subject={state.subject}
              body={state.body}
              date={state.date}
              attachments={state.attachments}
              initialEmailView={state.initialEmailView}
              handleInitialEmailView={handleInitialEmailView}
              handleMobileEmailView={handleMobileEmailView}
            />
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
              <Typography variant="h6" color="text.secondary" align="center">
                Please select an email from the list.
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    );
  } else if (isLaptopUp) {
    mainContent = (
      <Content>
        <Toolbar />
        <Box sx={{ display: 'flex', gap: 2, width: '100%' }}>
          <Box sx={{ flex: '0 0 30%', minWidth: 0 }}>
            <EmailList
              ref={emailListRef}
              getEmailProps={getEmailProps}
              handleMobileEmailView={handleMobileEmailView}
              handleInitialEmailView={handleInitialEmailView}
              time={state.time}
              mid={state.mid}
              folder={state.folder}
            />
          </Box>
          <Box sx={{ flex: '1 1 60%', minWidth: 0, display: 'flex', justifyContent: 'center' }}>
            {state.initialEmailView ? (
              <Email
                name={state.senderName}
                subject={state.subject}
                body={state.body}
                date={state.date}
                attachments={state.attachments}
                initialEmailView={state.initialEmailView}
                handleInitialEmailView={handleInitialEmailView}
                handleMobileEmailView={handleMobileEmailView}
              />
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 'calc(100vh - 200px)' }}>
                <Typography variant="h6" color="text.secondary" align="center">
                  Please select an email from the list.
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Content>
    );
  }

  // Drawer Content
  const drawer = (
    <div>
      <ComposeButton
        component="nav"
        variant="contained"
        color="primary"
        size="large"
        onClick={showCompose}
        startIcon={<CreateIcon />}
      >
        Compose
      </ComposeButton>
      <List component="nav" aria-label="mailbox list">
        <ListItem 
          button 
          selected={state.selectedIndex === 1} 
          onClick={() => handleFolder('Inbox', 1)}
          sx={{
            borderRadius: 1,
            '&.Mui-selected': {
              bgcolor: 'action.selected',
              '&:hover': { bgcolor: 'action.selected' },
            },
          }}
        >
          <ListItemIcon>
            <InboxIcon />
          </ListItemIcon>
          <ListItemText primary="Inbox" />
        </ListItem>
        <ListItem 
          button 
          selected={state.selectedIndex === 2} 
          onClick={() => handleFolder('Sent', 2)}
          sx={{
            borderRadius: 1,
            '&.Mui-selected': {
              bgcolor: 'action.selected',
              '&:hover': { bgcolor: 'action.selected' },
            },
          }}
        >
          <ListItemIcon>
            <SendIcon />
          </ListItemIcon>
          <ListItemText primary="Sent" />
        </ListItem>
      </List>
      <Divider />
    </div>
  );

 

  return (
    <Root>
      <SessionTimeout
        showModal={state.showModal}
        handleClose={handleClose}
        handleLogout={handleLogout}
      />
      <CssBaseline />

      <StyledAppBar position="fixed">
        <Toolbar>
          <MenuButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
          >
            <MenuIcon />
          </MenuButton>

          <Typography variant="h6" noWrap>
            {process.env.REACT_APP_APP_NAME}
          </Typography>

          {/* Compose icon on AppBar (mobile-only) */}
          {isMobile && (
            <IconButton
              color="inherit"
              aria-label="compose"
              edge="end"
              onClick={showCompose}
              sx={{ ml: 'auto' }}
            >
              <CreateIcon />
            </IconButton>
          )}

          {/* <HomeButton
            color="inherit"
            aria-label="go home"
            edge="end"
            onClick={handleHomeButton}
          >
            <HomeIcon />
          </HomeButton> */}
        </Toolbar>
      </StyledAppBar>

      {/* Temporary Drawer for mobile & tablet */}
      {!isLaptopUp && (
        <StyledDrawer
          container={container}
          variant="temporary"
          anchor="left"
          open={state.mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
        >
          <DrawerHeader>
            <IconButton onClick={handleDrawerToggle}>
              {theme.direction === 'ltr' ? <ChevronLeftIcon /> : <ChevronRightIcon />}
            </IconButton>
          </DrawerHeader>
          {drawer}
        </StyledDrawer>
      )}

      {/* Desktop Drawer */}
      {isLaptopUp && (
        <StyledDrawer variant="permanent" open anchor="left">
          <Toolbar />
          {drawer}
        </StyledDrawer>
      )}

      {/* Responsive Main Content */}
      {state.isCompose ? (
        <Content>
          <Compose name={state.userName} email={state.email} mid={state.mid} />
        </Content>
      ) : (
        mainContent
      )}
    </Root>
  );
};

export default MailboxList;